import { apiClient } from './apiClient';

export type BillingCategory =
  | 'marketing'
  | 'utility'
  | 'authentication'
  | 'service'
  | 'referral_conversion';

export interface BillingRates {
  marketing: number;
  utility: number;
  authentication: number;
  service: number;
  referral_conversion: number;
}

export interface BillingConfig {
  currency: string;
  rates: BillingRates;
  /** 0 disables budget tracking. */
  monthlyBudget: number;
  /** Percent of budget at which the UI starts warning. */
  budgetAlertThreshold: number;
}

export interface BudgetStatus {
  month: string;
  budget: number;
  currency: string;
  spent: number;
  remaining: number;
  usedPercent: number;
  threshold: number;
  enabled: boolean;
  overBudget: boolean;
  alerting: boolean;
}

/** One line of a breakdown — by category, template or campaign. */
export interface SpendRow {
  name: string;
  conversations: number;
  cost: number;
}

export interface SpendSummary {
  from: string;
  to: string;
  currency: string;
  totalCost: number;
  totalConversations: number;
  freeConversations: number;
  byCategory: Record<string, SpendRow>;
  byTemplate: Record<string, SpendRow>;
  byBroadcast: Record<string, SpendRow>;
  daily: { date: string; cost: number; conversations: number }[];
  rates: BillingRates;
  budget: BudgetStatus;
  /** False while every rate is still zero, so the UI can prompt for setup. */
  ratesConfigured: boolean;
}

/**
 * Spend reporting.
 *
 * Every figure is an estimate built from Meta's billable-conversation counts
 * multiplied by a locally configured rate card — it is not Meta's invoice, and
 * the UI must not present it as one.
 */
class BillingService {
  async summary(from: string, to: string): Promise<SpendSummary> {
    return apiClient.get<SpendSummary>(
      `/billing/summary?from=${from}&to=${to}`
    );
  }

  async getConfig(): Promise<BillingConfig> {
    return apiClient.get<BillingConfig>('/billing/config');
  }

  /** Admin-only server-side; an agent's call is rejected with 403. */
  async updateConfig(changes: {
    currency?: string;
    rates?: Partial<BillingRates>;
    monthlyBudget?: number;
    budgetAlertThreshold?: number;
  }): Promise<{ success: boolean; config: BillingConfig }> {
    return apiClient.patch('/billing/config', changes);
  }
}

export const billingService = new BillingService();
export default billingService;
